/**
 * Staff "Repeat weekly" panel for the daycare branch of the booking wizard
 * (migration 0096).
 *
 * Self-contained: owns its own state, calls POST /api/recurring-series/preview
 * for a live date preview (created vs skipped) and POST /api/recurring-series
 * to create the series, then navigates to the series page. The wizard only
 * mounts it.
 */
import {
  SESSION_OPTIONS,
  type SeriesPreview,
  type SessionType,
  WEEKDAY_OPTIONS,
  describeSkipReason,
  formatDay,
} from '@/lib/recurrence';
import { cn } from '@/lib/utils';
import { AlertTriangle, CalendarClock, Loader2, Repeat } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRecurringSeries, errorMessage, previewRecurringSeries } from './api';

interface Props {
  customerId: string;
  petIds: string[];
  facilityId: string;
  /** Pre-fill the start date from the wizard's chosen date. */
  defaultDate?: string | undefined;
  defaultSessionType?: SessionType | undefined;
}

export function RecurringDaycarePanel({
  customerId,
  petIds,
  facilityId,
  defaultDate,
  defaultSessionType,
}: Props) {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [startDate, setStartDate] = useState(defaultDate ?? '');
  const [endDate, setEndDate] = useState('');
  const [sessionType, setSessionType] = useState<SessionType>(defaultSessionType ?? 'full_day');
  const [dropOffTime, setDropOffTime] = useState('');
  const [pickUpTime, setPickUpTime] = useState('');
  const [preview, setPreview] = useState<SeriesPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (defaultDate && !startDate) setStartDate(defaultDate);
  }, [defaultDate, startDate]);

  const ready = enabled && weekdays.length > 0 && Boolean(startDate) && petIds.length > 0;

  const body = () => ({
    customerId,
    petIds,
    facilityId,
    sessionType,
    weekdays,
    startDate,
    endDate: endDate || null,
    dropOffTime: dropOffTime || null,
    pickUpTime: pickUpTime || null,
  });

  // Live preview — debounced on rule changes.
  useEffect(() => {
    if (!ready) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setError(null);
    const t = setTimeout(() => {
      previewRecurringSeries(body())
        .then((p) => {
          if (!cancelled) setPreview(p);
        })
        .catch((e) => {
          if (!cancelled) {
            setPreview(null);
            setError(errorMessage(e));
          }
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, weekdays.join(','), startDate, endDate, sessionType, petIds.join(','), facilityId]);

  const toggleWeekday = (d: number) =>
    setWeekdays((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort()));

  const submit = async () => {
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await createRecurringSeries(body());
      const first = r.series[0]?.series.id;
      navigate(first ? `/recurring-series/${first}` : `/customers/${customerId}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-6 rounded-xl bg-surface ring-1 ring-border elevate">
      <label className="flex cursor-pointer items-center gap-3 px-5 py-4">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="size-4 accent-primary"
        />
        <Repeat className="size-4 text-primary" strokeWidth={1.75} aria-hidden />
        <span className="text-sm font-medium text-foreground">Repeat weekly</span>
        <span className="text-xs text-muted-foreground">
          Book the same days every week — up to 12 weeks ahead, extended automatically.
        </span>
      </label>

      {enabled && (
        <div className="border-t border-border px-5 py-4">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Days of the week
              </p>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Days of the week">
                {WEEKDAY_OPTIONS.map((w) => {
                  const on = weekdays.includes(w.value);
                  return (
                    <button
                      key={w.value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleWeekday(w.value)}
                      className={cn(
                        'rounded-full px-3 py-1.5 text-xs font-medium press ring-hairline',
                        on ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground',
                      )}
                    >
                      {w.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Session
              </p>
              <select
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value as SessionType)}
                aria-label="Session type"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                {SESSION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="First visit" htmlFor="rec-start">
                <input
                  id="rec-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Until (optional)" htmlFor="rec-end">
                <input
                  id="rec-end"
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Drop-off (optional)" htmlFor="rec-drop">
                <input
                  id="rec-drop"
                  type="time"
                  value={dropOffTime}
                  onChange={(e) => setDropOffTime(e.target.value)}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Pick-up (optional)" htmlFor="rec-pick">
                <input
                  id="rec-pick"
                  type="time"
                  value={pickUpTime}
                  onChange={(e) => setPickUpTime(e.target.value)}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                />
              </Field>
            </div>
          </div>

          <PreviewList preview={preview} loading={previewLoading} ready={ready} />

          {error && (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-destructive">
              <AlertTriangle className="size-4 shrink-0" strokeWidth={1.75} />
              {error}
            </p>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Creates the series now instead of a one-off visit. Each date is checked for capacity
              and vaccinations individually.
            </p>
            <button
              type="button"
              disabled={!ready || submitting || previewLoading || (preview?.ok.length ?? 0) === 0}
              onClick={submit}
              className="flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <CalendarClock className="size-4" strokeWidth={1.75} />
              )}
              Book weekly series
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export function PreviewList({
  preview,
  loading,
  ready,
}: {
  preview: SeriesPreview | null;
  loading: boolean;
  ready: boolean;
}) {
  if (!ready) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        Pick at least one day and a first visit date to preview the dates.
      </p>
    );
  }
  if (loading && !preview) {
    return (
      <div className="mt-4 space-y-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-8 animate-pulse rounded-md bg-secondary"
            style={{ opacity: 1 - i * 0.25 }}
          />
        ))}
      </div>
    );
  }
  if (!preview) return null;
  const skippedByDate = new Map(preview.skipped.map((s) => [s.date, s.reason]));
  return (
    <div className="mt-4">
      <div className="mb-2 flex items-baseline justify-between">
        <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Preview — next {preview.horizonWeeks} weeks
        </p>
        <p className="text-xs text-muted-foreground">
          <span className="num font-medium text-foreground">{preview.ok.length}</span> to book
          {preview.skipped.length > 0 && (
            <>
              {' · '}
              <span className="num font-medium text-warning-foreground">
                {preview.skipped.length}
              </span>{' '}
              skipped
            </>
          )}
        </p>
      </div>
      {preview.dates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No dates match that rule.</p>
      ) : (
        <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {preview.dates.map((d) => {
            const reason = skippedByDate.get(d);
            return (
              <li
                key={d}
                className={cn(
                  'flex items-center justify-between rounded-md px-3 py-1.5 text-sm ring-hairline',
                  reason ? 'bg-warning/15 text-warning-foreground' : 'bg-secondary text-foreground',
                )}
              >
                <span className="num">{formatDay(d)}</span>
                <span className="text-xs">{reason ? describeSkipReason(reason) : 'Available'}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
